/**
 * نموذج الصلاحيات التشغيلية في لوحة الفرع.
 * الصلاحيات هي المصدر الوحيد لظهور الصفحات والأزرار — لا يرتبط أي مسمى وظيفي بصلاحيات ثابتة.
 */

export type PermissionKey =
  | "receiveOrders"
  | "updateOrderStatus"
  | "pos"
  | "productAvailability"
  | "editQuantities"
  | "customerRatings"
  | "workingHours"
  | "pickupPolicy"
  | "emergencyClosure"
  | "reports";

export type PermissionSet = Record<PermissionKey, boolean>;

export interface PermissionMeta {
  key: PermissionKey;
  label: string;
  icon: string;
}

/** الصلاحيات التشغيلية الحالية فقط — لا تُضاف صلاحيات جديدة. */
export const PERMISSION_LIST: PermissionMeta[] = [
  { key: "receiveOrders", label: "استقبال طلبات الفرع", icon: "ri-inbox-archive-line" },
  { key: "updateOrderStatus", label: "تحديث حالات الطلبات", icon: "ri-refresh-line" },
  { key: "pos", label: "نقطة البيع POS وإصدار الفاتورة", icon: "ri-computer-line" },
  { key: "productAvailability", label: "التحكم في توفر المنتجات", icon: "ri-toggle-line" },
  { key: "editQuantities", label: "تعديل الكميات المتاحة", icon: "ri-stack-line" },
  { key: "customerRatings", label: "تقييم العملاء", icon: "ri-user-star-line" },
  { key: "workingHours", label: "تعديل أوقات عمل الفرع", icon: "ri-time-line" },
  { key: "pickupPolicy", label: "تعديل سياسة الاستلام", icon: "ri-handbag-line" },
  { key: "emergencyClosure", label: "الفتح والإغلاق الطارئ", icon: "ri-alarm-warning-line" },
  { key: "reports", label: "عرض تقارير أداء الفرع", icon: "ri-bar-chart-2-line" },
];

export const EMPTY_PERMISSIONS: PermissionSet = {
  receiveOrders: false,
  updateOrderStatus: false,
  pos: false,
  productAvailability: false,
  editQuantities: false,
  customerRatings: false,
  workingHours: false,
  pickupPolicy: false,
  emergencyClosure: false,
  reports: false,
};

/** هل يملك الموظف أي صلاحية تشغيلية واحدة على الأقل؟ */
export function hasAnyOperational(permissions: PermissionSet): boolean {
  return PERMISSION_LIST.some((item) => permissions[item.key]);
}

export function canViewOrders(permissions: PermissionSet): boolean {
  return permissions.receiveOrders || permissions.updateOrderStatus;
}

export function canViewMenu(permissions: PermissionSet): boolean {
  return permissions.productAvailability || permissions.editQuantities;
}

export function canViewSettings(permissions: PermissionSet): boolean {
  return permissions.workingHours || permissions.pickupPolicy || permissions.emergencyClosure;
}

export function canViewReports(permissions: PermissionSet): boolean {
  return permissions.reports;
}

/** الرئيسية تظهر لأي موظف يملك صلاحية تشغيلية واحدة على الأقل. */
export function canViewDashboard(permissions: PermissionSet): boolean {
  return hasAnyOperational(permissions);
}

/**
 * أول صفحة مسموح للموظف بها بعد تسجيل الدخول.
 * إن لم يملك أي صلاحية تشغيلية يُوجَّه إلى شاشة «لا توجد صلاحيات».
 */
export function landingForPermissions(permissions: PermissionSet): string {
  if (canViewDashboard(permissions)) return "/dashboard";
  if (canViewOrders(permissions)) return "/orders";
  if (permissions.pos) return "/pos";
  if (canViewMenu(permissions)) return "/menu";
  if (canViewSettings(permissions)) return "/settings";
  if (canViewReports(permissions)) return "/reports";
  return "/no-permissions";
}